import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { formatApiError } from '../utils/apiError';

type BatchStatus = 'queued' | 'running' | 'completed' | 'failed';

type RowResult = {
  row_index: number;
  status: string;
  reservation_id?: string | null;
  error?: string | null;
  note?: string | null;
};

type Batch = {
  batch_id: string;
  status: BatchStatus;
  filename?: string | null;
  total_rows: number;
  completed_rows: number;
  failed_rows: number;
  current_row: number;
  current_action: string;
  results: RowResult[];
  has_output: boolean;
  error?: string | null;
};

const ACTIVE = new Set<BatchStatus>(['queued', 'running']);
const RITZ_CSV_MAX_BYTES = 2 * 1024 * 1024;

function badgeVariant(status: string): string {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running') return 'primary';
  return 'secondary';
}

export default function RitzReservationDemo(): React.ReactElement {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [batch, setBatch] = useState<Batch | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const active = Boolean(batch && ACTIVE.has(batch.status));

  const fetchBatch = useCallback(async (batchId: string) => {
    const response = await apiClient.get<Batch>(API_ENDPOINTS.RITZ_BATCH_RUN(batchId));
    setBatch(response.data);
  }, []);

  useEffect(() => {
    if (!batch?.batch_id || !active) return undefined;
    const timer = window.setInterval(() => {
      fetchBatch(batch.batch_id).catch((e: unknown) => setError(formatApiError(e, 'Could not refresh batch status.')));
    }, 3000);
    return () => window.clearInterval(timer);
  }, [active, fetchBatch, batch?.batch_id]);

  const progress = useMemo(() => {
    if (!batch || batch.total_rows === 0) return 0;
    return Math.round(((batch.completed_rows + batch.failed_rows) / batch.total_rows) * 100);
  }, [batch]);

  async function start(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Enter both Res Agent username and password.');
      return;
    }
    if (!file) {
      setError('Choose a CSV file to upload.');
      return;
    }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('username', username.trim());
      form.append('password', password);
      form.append('file', file);
      const response = await apiClient.post<Batch>(API_ENDPOINTS.RITZ_BATCH_RUNS, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPassword('');
      setBatch(response.data);
    } catch (e: unknown) {
      setError(formatApiError(e, 'Could not start the batch.'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const selected = event.target.files?.[0] || null;
    if (!selected) {
      setFile(null);
      return;
    }
    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setFile(null);
      setError('Choose a CSV file to upload.');
      event.target.value = '';
      return;
    }
    if (selected.size === 0) {
      setFile(null);
      setError(`"${selected.name}" is empty and cannot be uploaded.`);
      event.target.value = '';
      return;
    }
    if (selected.size > RITZ_CSV_MAX_BYTES) {
      setFile(null);
      setError(`"${selected.name}" is too large. Reservation CSV files must be 2 MB or smaller.`);
      event.target.value = '';
      return;
    }
    setError('');
    setFile(selected);
  }

  async function downloadOutput(): Promise<void> {
    if (!batch) return;
    try {
      const response = await apiClient.get(API_ENDPOINTS.RITZ_BATCH_OUTPUT(batch.batch_id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data as BlobPart]));
      const link = document.createElement('a');
      link.href = url;
      const base = (batch.filename || 'batch').replace(/\.[^.]+$/, '');
      link.download = `${base}_results.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      setError(formatApiError(e, 'Could not download the results CSV.'));
    }
  }

  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '24px 0 48px' }}>
      <div className="mb-4">
        <h1 className="h3 mb-2">Ritz Reservation Demo</h1>
        <p className="text-muted mb-0">
          Upload a CSV — each row becomes one real reservation, processed one at a time. Rows that fail are skipped;
          the results CSV adds a <strong>Reservation ID</strong> column and reflects the effective (normalized) values used.
        </p>
      </div>

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form onSubmit={start}>
            <Form.Group className="mb-3">
              <Form.Label>Target application</Form.Label>
              <Form.Control readOnly value="https://dev-reservations.ritz-carltonyachtcollection.com/touch/vx-history" />
            </Form.Group>
            <div className="row g-2 align-items-end">
              <Form.Group className="col-md-4" controlId="ritz-batch-username">
                <Form.Label>Res Agent username</Form.Label>
                <Form.Control value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" disabled={active || submitting} required />
              </Form.Group>
              <Form.Group className="col-md-4" controlId="ritz-batch-password">
                <Form.Label>Password</Form.Label>
                <Form.Control type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" disabled={active || submitting} required />
              </Form.Group>
              <Form.Group className="col-md-4" controlId="ritz-batch-file">
                <Form.Label>Reservations CSV (max 50 rows)</Form.Label>
                <Form.Control ref={fileInputRef as never} type="file" accept=".csv,text/csv" disabled={active || submitting} onChange={handleFileChange} required />
              </Form.Group>
            </div>
            <div className="mt-3">
              <Button type="submit" disabled={active || submitting}>
                {(submitting || active) && <Spinner animation="border" size="sm" className="me-2" />}
                {active ? 'Running…' : 'Run batch'}
              </Button>
            </div>
            <Form.Text className="text-muted d-block mt-2">
              Credentials are used only to establish a private browser session and are never stored. Guest data comes from the
              CSV. Every row creates a real reservation on the Ritz-Carlton Yacht Collection system — use authorized test data only.
            </Form.Text>
          </Form>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger">{error}</Alert>}

      {batch && (
        <Card className="shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
              <div>
                <h2 className="h5 mb-1">Batch run</h2>
                <div className="small text-muted">{batch.filename}</div>
              </div>
              <Badge bg={badgeVariant(batch.status)}>{batch.status.toUpperCase()}</Badge>
            </div>
            <ProgressBar now={progress} className="mb-2" />
            <div className="small text-muted mb-2">
              {batch.completed_rows + batch.failed_rows} of {batch.total_rows} rows processed
              {' · '}<span className="text-success">{batch.completed_rows} succeeded</span>
              {' · '}<span className="text-danger">{batch.failed_rows} failed</span>
            </div>
            <div className="small mb-3"><strong>Current action:</strong> {batch.current_action}</div>
            {batch.error && <Alert variant="danger">{batch.error}</Alert>}

            {batch.has_output && (
              <Button variant="outline-success" size="sm" className="mb-3" onClick={downloadOutput}>
                Download results CSV
              </Button>
            )}

            {batch.results.length > 0 && (
              <Table size="sm" bordered responsive className="align-middle">
                <thead>
                  <tr><th>Row</th><th>Status</th><th>Reservation ID</th><th>Notes / Error</th></tr>
                </thead>
                <tbody>
                  {batch.results.map((r) => (
                    <tr key={r.row_index}>
                      <td>{r.row_index}</td>
                      <td><Badge bg={badgeVariant(r.status)}>{r.status}</Badge></td>
                      <td>{r.reservation_id || '—'}</td>
                      <td className="small text-muted">{r.error || r.note || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
